import { css, useTheme } from '@emotion/react';
import { Folder as FolderIcon } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { DeleteFolderDialog } from '@/components/layout/delete-folder-dialog';
import { FolderDialog } from '@/components/layout/folder-dialog';
import type { Folder } from '@/lib/api-client';
import { createFolder, deleteFolder, updateFolder } from '@/lib/api-client';
import { fontSizes, shadows, spacing } from '@/theme/tokens';

interface FolderTabsProps {
  folders: Folder[];
  activeFolder: string | null;
  onFolderChange: (folderId: string | null) => void;
  onFoldersChanged: () => void;
  totalScriptCount: number;
}

export function FolderTabs({
  folders,
  activeFolder,
  onFolderChange,
  onFoldersChanged,
  totalScriptCount,
}: FolderTabsProps) {
  const theme = useTheme();
  const [contextMenu, setContextMenu] = useState<{
    folderId: string;
    x: number;
    y: number;
  } | null>(null);
  const [dialogMode, setDialogMode] = useState<'create' | 'rename' | null>(null);
  const [targetFolder, setTargetFolder] = useState<Folder | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Folder | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const contextMenuRef = useRef<HTMLDivElement>(null);

  // Close context menu on outside click or Escape
  useEffect(() => {
    if (!contextMenu) return;
    function handleClick(e: MouseEvent) {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target as Node)) {
        setContextMenu(null);
      }
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setContextMenu(null);
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [contextMenu]);

  const handleContextMenu = useCallback((e: React.MouseEvent, folderId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ folderId, x: e.clientX, y: e.clientY });
  }, []);

  const handleCreate = useCallback(
    async (name: string) => {
      setIsSubmitting(true);
      try {
        await createFolder(name);
        onFoldersChanged();
        setDialogMode(null);
      } finally {
        setIsSubmitting(false);
      }
    },
    [onFoldersChanged],
  );

  const handleRename = useCallback(
    async (name: string) => {
      if (!targetFolder) return;
      setIsSubmitting(true);
      try {
        await updateFolder(targetFolder.id, name);
        onFoldersChanged();
        setDialogMode(null);
        setTargetFolder(null);
      } finally {
        setIsSubmitting(false);
      }
    },
    [targetFolder, onFoldersChanged],
  );

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await deleteFolder(deleteTarget.id);
      onFoldersChanged();
      if (activeFolder === deleteTarget.id) {
        onFolderChange(null);
      }
      setDeleteTarget(null);
    } finally {
      setIsDeleting(false);
    }
  }, [deleteTarget, activeFolder, onFolderChange, onFoldersChanged]);

  const allScriptsTabStyles = css`
    display: flex;
    align-items: center;
    gap: ${spacing.sm};
    padding: ${spacing.sm} ${spacing.md};
    cursor: pointer;
    flex-shrink: 0;
    border: none;
    background: none;
    font-size: ${fontSizes.sm};
    color: ${activeFolder === null ? theme.colors.primary : theme.colors.text};
    font-weight: ${activeFolder === null ? 700 : 400};
    border-bottom: ${activeFolder === null
      ? `3px solid ${theme.colors.primary}`
      : '3px solid transparent'};
    transition: color 0.2s ease-in-out;

    &:hover {
      background: ${theme.colors.sidebarHoverBg};
    }
  `;

  const badgeStyles = css`
    font-size: ${fontSizes.xs};
    background: ${theme.colors.primary}26;
    color: ${theme.colors.primary};
    border-radius: 8px;
    padding: 0 6px;
    line-height: 1.5;
  `;

  return (
    <>
      <div
        data-testid="folder-tabs"
        role="tablist"
        css={css`
          display: flex;
          flex-wrap: nowrap;
          overflow-x: auto;
          border-bottom: 1px solid ${theme.colors.border};

          &::-webkit-scrollbar {
            display: none;
          }
        `}
      >
        {/* All Scripts tab */}
        <button
          role="tab"
          aria-selected={activeFolder === null}
          css={allScriptsTabStyles}
          onClick={() => onFolderChange(null)}
        >
          All Scripts
          <span css={badgeStyles}>{totalScriptCount}</span>
        </button>

        {/* Folder tabs */}
        {folders.map((folder) => (
          <div
            key={folder.id}
            data-testid={`folder-tab-${folder.id}`}
            css={css`
              display: flex;
              align-items: center;
              flex-shrink: 0;
              border-bottom: ${activeFolder === folder.id
                ? `3px solid ${theme.colors.primary}`
                : '3px solid transparent'};
            `}
          >
            <button
              role="tab"
              aria-selected={activeFolder === folder.id}
              onClick={() => onFolderChange(folder.id)}
              onContextMenu={(e) => handleContextMenu(e, folder.id)}
              css={css`
                display: flex;
                align-items: center;
                gap: ${spacing.sm};
                padding: ${spacing.sm} ${spacing.md};
                cursor: pointer;
                border: none;
                background: none;
                font-size: ${fontSizes.sm};
                color: ${activeFolder === folder.id ? theme.colors.primary : theme.colors.text};
                font-weight: ${activeFolder === folder.id ? 700 : 400};
                transition: color 0.2s ease-in-out;

                &:hover {
                  background: ${theme.colors.sidebarHoverBg};
                }
              `}
            >
              <FolderIcon size={16} aria-hidden="true" />
              {folder.name}
              <span css={badgeStyles}>{folder.scriptCount}</span>
            </button>
          </div>
        ))}

        {/* New folder button */}
        <button
          aria-label="New folder"
          onClick={() => setDialogMode('create')}
          css={css`
            display: flex;
            align-items: center;
            justify-content: center;
            padding: ${spacing.sm} ${spacing.md};
            cursor: pointer;
            flex-shrink: 0;
            border: none;
            background: none;
            color: ${theme.colors.textSecondary};
            font-size: ${fontSizes.lg};
            line-height: 1;
            transition: color 0.2s ease-in-out;

            &:hover {
              color: ${theme.colors.primary};
              background: ${theme.colors.sidebarHoverBg};
            }
          `}
        >
          +
        </button>
      </div>

      {/* Right-click context menu (fixed position) */}
      {contextMenu && (
        <div
          ref={contextMenuRef}
          data-testid="folder-context-menu"
          css={css`
            position: fixed;
            top: ${contextMenu.y}px;
            left: ${contextMenu.x}px;
            background: ${theme.colors.surfaceElevated};
            border: 1px solid ${theme.colors.border};
            border-radius: 8px;
            box-shadow: ${shadows.elevated};
            z-index: 50;
            min-width: 140px;
            padding: ${spacing.xs} 0;
          `}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              const folder = folders.find((f) => f.id === contextMenu.folderId);
              setContextMenu(null);
              if (folder) {
                setTargetFolder(folder);
                setDialogMode('rename');
              }
            }}
            css={css`
              display: block;
              width: 100%;
              text-align: left;
              background: none;
              border: none;
              padding: ${spacing.sm} ${spacing.md};
              cursor: pointer;
              font-size: ${fontSizes.sm};
              color: ${theme.colors.text};

              &:hover {
                background: ${theme.colors.sidebarHoverBg};
              }
            `}
          >
            Rename
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              const folder = folders.find((f) => f.id === contextMenu.folderId);
              setContextMenu(null);
              if (folder) {
                setDeleteTarget(folder);
              }
            }}
            css={css`
              display: block;
              width: 100%;
              text-align: left;
              background: none;
              border: none;
              padding: ${spacing.sm} ${spacing.md};
              cursor: pointer;
              font-size: ${fontSizes.sm};
              color: ${theme.colors.error};

              &:hover {
                background: ${theme.colors.sidebarHoverBg};
              }
            `}
          >
            Delete
          </button>
        </div>
      )}

      {/* Create / Rename dialog */}
      <FolderDialog
        isOpen={dialogMode !== null}
        title={dialogMode === 'rename' ? 'Rename Folder' : 'New Folder'}
        initialName={dialogMode === 'rename' && targetFolder ? targetFolder.name : ''}
        isSubmitting={isSubmitting}
        onSubmit={dialogMode === 'rename' ? handleRename : handleCreate}
        onCancel={() => {
          setDialogMode(null);
          setTargetFolder(null);
        }}
      />

      {/* Delete confirmation dialog */}
      <DeleteFolderDialog
        folderName={deleteTarget?.name ?? ''}
        isOpen={deleteTarget !== null}
        isDeleting={isDeleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
}
